export type DispatchService = 'transportation' | 'food' | 'grocery' | 'porter' | 'on_demand';

export type ServiceTab = 'all' | DispatchService;

/** Booking drawer mode — transportation maps to ride. */
export type DispatchBookMode = 'ride' | 'food' | 'grocery' | 'porter' | 'on_demand';

export function serviceTabToBookMode(tab: ServiceTab): DispatchBookMode | null {
  if (tab === 'all') return null;
  if (tab === 'transportation') return 'ride';
  return tab;
}

/** Book mode for the currently selected dispatcher service. Falls back to the first enabled module. */
export function resolveDispatchBookMode(tab: ServiceTab, enabled: DispatchBookMode[]): DispatchBookMode {
  const mapped = serviceTabToBookMode(tab);
  if (mapped && enabled.includes(mapped)) return mapped;
  return enabled[0] || 'ride';
}

/** Service-specific booking CTA / drawer copy. */
export interface DispatchBookCopy {
  /** Empty-panel CTA, e.g. "Create New Ride Booking" */
  createNew: string;
  /** Header button and drawer title, e.g. "New Ride Booking" */
  newBooking: string;
}

export const DISPATCH_BOOK_COPY: Record<DispatchBookMode, DispatchBookCopy> = {
  ride: { createNew: 'Create New Ride Booking', newBooking: 'New Ride Booking' },
  food: { createNew: 'Create New Food Booking', newBooking: 'New Food Booking' },
  grocery: { createNew: 'Create New Grocery Booking', newBooking: 'New Grocery Booking' },
  porter: { createNew: 'Create New Porter Booking', newBooking: 'New Porter Booking' },
  on_demand: { createNew: 'Create New Home Service Booking', newBooking: 'New Home Service Booking' },
};

export function dispatchBookCopy(mode: DispatchBookMode): DispatchBookCopy {
  return DISPATCH_BOOK_COPY[mode];
}

export type QueueStatusTab = 'action' | 'active' | 'done' | 'cancelled';

export interface DispatchRow {
  _id: string;
  service: DispatchService;
  ref: string;
  customer: string;
  customerPhone: string;
  partner: string;
  partnerId?: string;
  status: string;
  total: number;
  createdAt: string;
  scheduledAt?: string;
  vehicleType?: string;
  paymentMethod?: string;
  pickupAddress?: string;
  dropoffAddress?: string;
  pickupLat?: number;
  pickupLng?: number;
  dropoffLat?: number;
  dropoffLng?: number;
  raw: Record<string, unknown>;
}

export function isLiveTrip(row: DispatchRow): boolean {
  return STATUS_BY_TAB.active.includes(row.status.toLowerCase());
}

export function extractPartnerId(raw: Record<string, unknown>, service: DispatchRow['service']): string | undefined {
  const pick = (v: unknown): string | undefined => {
    if (!v) return undefined;
    if (typeof v === 'string') return v;
    if (typeof v === 'object' && v !== null && '_id' in v) return String((v as { _id: unknown })._id);
    return undefined;
  };
  if (service === 'transportation') return pick(raw.driverId);
  if (service === 'on_demand') return pick(raw.providerId);
  return pick(raw.deliveryPartnerId) || pick(raw.partnerId);
}

export const QUEUE_STATUS_TABS: { key: QueueStatusTab; label: string; shortLabel: string; color: string }[] = [
  { key: 'action', label: 'Needs Action', shortLabel: 'Action', color: '#f59e0b' },
  { key: 'active', label: 'In Progress', shortLabel: 'Active', color: '#3b82f6' },
  { key: 'done', label: 'Completed', shortLabel: 'Done', color: '#22c55e' },
  { key: 'cancelled', label: 'Cancelled', shortLabel: 'Cancel', color: '#ef4444' },
];

export const STATUS_BY_TAB: Record<QueueStatusTab, string[]> = {
  action: ['pending', 'searching', 'bidding', 'confirmed'],
  active: [
    'accepted', 'arrived', 'started',
    'assigned', 'preparing', 'ready', 'picked_up', 'delivering', 'in_progress',
    'en_route', 'paused',
    'driver_assigned', 'arriving', 'reached_pickup', 'loading', 'reached_destination', 'unloading',
  ],
  done: ['completed', 'delivered'],
  cancelled: ['cancelled', 'refunded', 'rejected', 'expired'],
};

export function formatAddress(addr: unknown): string | undefined {
  if (!addr) return undefined;
  if (typeof addr === 'string') return addr.trim() || undefined;
  const o = addr as Record<string, unknown>;
  const primary = [o.formatted, o.formattedAddress, o.fullAddress, o.address, o.street, o.line1]
    .find((p) => typeof p === 'string' && p.trim()) as string | undefined;
  const extra = [o.line2, o.landmark, o.area, o.city, o.state, o.pincode, o.zip, o.postalCode, o.country]
    .filter((p) => typeof p === 'string' && String(p).trim())
    .map((p) => String(p).trim());
  if (primary) {
    const base = primary.trim();
    const extraStr = extra.filter((e) => !base.toLowerCase().includes(e.toLowerCase())).join(', ');
    return extraStr ? `${base}, ${extraStr}` : base;
  }
  return extra.length ? extra.join(', ') : undefined;
}

export function extractCoordinates(source: unknown): { lat?: number; lng?: number } {
  if (!source || typeof source !== 'object') return {};
  const o = source as Record<string, unknown>;

  if (Array.isArray(o.coordinates) && o.coordinates.length >= 2) {
    const lng = Number(o.coordinates[0]);
    const lat = Number(o.coordinates[1]);
    if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
  }
  if (o.location && typeof o.location === 'object') {
    const nested = extractCoordinates(o.location);
    if (nested.lat != null && nested.lng != null) return nested;
  }
  if (typeof o.lat === 'number' && typeof o.lng === 'number') {
    return { lat: o.lat, lng: o.lng };
  }
  if (typeof o.latitude === 'number' && typeof o.longitude === 'number') {
    return { lat: o.latitude, lng: o.longitude };
  }
  return {};
}

export function applyCoordinates(
  row: Pick<DispatchRow, 'pickupLat' | 'pickupLng' | 'dropoffLat' | 'dropoffLng'>,
  pickup?: unknown,
  dropoff?: unknown,
) {
  const p = extractCoordinates(pickup);
  const d = extractCoordinates(dropoff);
  if (p.lat != null && p.lng != null) {
    row.pickupLat = p.lat;
    row.pickupLng = p.lng;
  }
  if (d.lat != null && d.lng != null) {
    row.dropoffLat = d.lat;
    row.dropoffLng = d.lng;
  }
}

export const SERVICE_FILTERS: { value: ServiceTab; label: string; short: string }[] = [
  { value: 'all', label: 'All Services', short: 'All' },
  { value: 'transportation', label: 'Transport', short: 'Rides' },
  { value: 'food', label: 'Food', short: 'Food' },
  { value: 'grocery', label: 'Grocery', short: 'Grocery' },
  { value: 'porter', label: 'Porter', short: 'Porter' },
  { value: 'on_demand', label: 'Services', short: 'Services' },
];

export function statusToTab(status: string): QueueStatusTab {
  const s = status.toLowerCase();
  for (const [tab, statuses] of Object.entries(STATUS_BY_TAB) as [QueueStatusTab, string[]][]) {
    if (statuses.includes(s)) return tab;
  }
  return 'action';
}

export const SERVICE_META: Record<DispatchService, { label: string; color: string }> = {
  transportation: { label: 'Transport', color: '#2563eb' },
  food: { label: 'Food', color: '#ea580c' },
  grocery: { label: 'Grocery', color: '#16a34a' },
  porter: { label: 'Porter', color: '#0f766e' },
  on_demand: { label: 'Services', color: '#0891b2' },
};

/** Needs-action queue tab and no assignee ref (matches backend `jobsNeedingAssignment`). */
export function rowNeedsAssignment(row: Pick<DispatchRow, 'status' | 'partnerId'>): boolean {
  return STATUS_BY_TAB.action.includes(row.status.toLowerCase()) && !row.partnerId;
}

export function countRowsNeedingAssignment(rows: Pick<DispatchRow, 'status' | 'partnerId'>[]): number {
  return rows.filter(rowNeedsAssignment).length;
}

export function countRowsByQueueTab(rows: Pick<DispatchRow, 'status' | 'partnerId'>[]): Record<QueueStatusTab, number> {
  const counts: Record<QueueStatusTab, number> = { action: 0, active: 0, done: 0, cancelled: 0 };
  rows.forEach((row) => {
    const tab = statusToTab(row.status);
    if (tab === 'action') {
      if (rowNeedsAssignment(row)) counts.action += 1;
      return;
    }
    counts[tab] += 1;
  });
  return counts;
}

export function userLabel(u: unknown): { name: string; phone: string } {
  if (!u) return { name: '-', phone: '' };
  if (typeof u === 'string') return { name: u.slice(-6), phone: '' };
  const o = u as { firstName?: string; lastName?: string; phone?: string; email?: string; name?: string };
  return {
    name: o.name || [o.firstName, o.lastName].filter(Boolean).join(' ') || o.email || '-',
    phone: o.phone || '',
  };
}

export function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export function formatStatus(status: string): string {
  return status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
